/**
 * ZefixRadarPage — Firmen-Radar: Schweizer Handelsregister durchsuchen (Zefix)
 * und Treffer als Akquise-Leads übernehmen (z.B. Immobilienfirmen anschreiben).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Radar, Search, Loader2, ExternalLink, UserPlus, MapPin, ChevronDown, ChevronUp, BellPlus, Play, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import PageHelp from "@/components/PageHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ResultRow({ r, companyId, onTake }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setOpen((o) => !o);
    if (!detail) {
      try {
        const res = await axios.get(`${API}/zefix/firm/${r.ehraid}`, { withCredentials: true });
        setDetail(res.data);
      } catch (e) { toast.error("Detail konnte nicht geladen werden"); }
    }
  };

  const take = async () => {
    if (!companyId) { toast.error("Bitte zuerst eine eigene Firma (Mandant) wählen"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/zefix/akquise-lead`, { ehraid: r.ehraid, company_id: companyId }, { withCredentials: true });
      toast.success(res.data.message);
      onTake?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übernahme fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[var(--studio-border)] rounded-lg bg-white" data-testid={`zefix-row-${r.ehraid}`}>
      <div className="flex flex-wrap items-center gap-2 p-3">
        <button onClick={toggle} className="flex-1 min-w-[220px] text-left" data-testid={`zefix-toggle-${r.ehraid}`}>
          <span className="text-sm font-medium">{r.name}</span>
          <span className="text-xs text-slate-500 ml-2">{r.seat}{r.legal_form ? ` · ${r.legal_form}` : ""}</span>
        </button>
        <Badge className={`text-[10px] ${r.active ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-600 border-red-200"}`}>
          {r.active ? "Aktiv" : r.status}
        </Badge>
        {r.distance_km != null && <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200">{r.distance_km} km</Badge>}
        <span className="text-[10px] text-slate-400 font-mono">{r.uid}</span>
        {r.lead_taken ? (
          <>
            <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`zefix-taken-${r.ehraid}`}>
              ✓ Als Lead übernommen
            </Badge>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => (window.location.href = "/leads")} data-testid={`zefix-open-lead-${r.ehraid}`}>
              Lead öffnen
            </Button>
          </>
        ) : (
          <Button size="sm" className="h-7 text-xs bg-[#2563EB] hover:bg-[#1d4ed8]" onClick={take} disabled={busy} data-testid={`zefix-take-${r.ehraid}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3 mr-1" />} Als Lead übernehmen
          </Button>
        )}
        <button onClick={toggle}>{open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}</button>
      </div>
      {open && (
        <div className="px-4 pb-3 border-t border-[var(--studio-border)] pt-2 text-xs space-y-1.5">
          {!detail ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
            <>
              <p className="flex items-center gap-1.5 text-slate-600">
                <MapPin className="w-3 h-3 shrink-0" />
                {detail.address?.street}, {detail.address?.zip} {detail.address?.town}
              </p>
              {detail.purpose && <p className="text-slate-500 line-clamp-3">{detail.purpose}</p>}
              {detail.excerpt_url && (
                <a href={detail.excerpt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2563EB] font-medium">
                  <ExternalLink className="w-3 h-3" /> Handelsregister-Auszug öffnen
                </a>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function WatchSection({ companies, companyId }) {
  const [watches, setWatches] = useState([]);
  const [nw, setNw] = useState({ name: "", keywords: "", cantons: "ZH", radius_km: "" });
  const [busy, setBusy] = useState("");

  const load = () => axios.get(`${API}/zefix/watches`, { withCredentials: true }).then((r) => setWatches(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!companyId) { toast.error("Ziel-Firma oben wählen"); return; }
    setBusy("create");
    try {
      await axios.post(`${API}/zefix/watches`, {
        name: nw.name || "Neugründungs-Alarm",
        company_id: companyId,
        keywords: nw.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        cantons: nw.cantons.split(",").map((s) => s.trim()).filter(Boolean),
        radius_km: nw.radius_km ? parseInt(nw.radius_km) : 0,
      }, { withCredentials: true });
      toast.success("Alarm angelegt — prüft täglich automatisch");
      setNw({ name: "", keywords: "", cantons: "ZH", radius_km: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen"); }
    setBusy("");
  };

  const runNow = async (id) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/zefix/watches/${id}/run?days_back=7`, {}, { withCredentials: true });
      toast.success(`${r.data.checked} Neueintragungen geprüft → ${r.data.created} neue Akquise-Leads`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Lauf fehlgeschlagen"); }
    setBusy("");
  };

  const toggle = async (w) => { await axios.put(`${API}/zefix/watches/${w.id}`, { enabled: !w.enabled }, { withCredentials: true }); load(); };
  const del = async (id) => { await axios.delete(`${API}/zefix/watches/${id}`, { withCredentials: true }); toast.success("Alarm gelöscht"); load(); };
  const companyName = (cid) => companies.find((c) => c.id === cid)?.name || "?";

  return (
    <Card data-testid="zefix-watch-section">
      <CardContent className="pt-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <BellPlus className="w-4 h-4 text-[#2563EB]" />
          <h2 className="text-base font-semibold">Neugründungs-Alarm</h2>
          <span className="text-xs text-slate-500">prüft täglich neue HR-Einträge (SHAB) → automatische Akquise-Leads</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <Input placeholder="Alarm-Name (z.B. Immobilien ZH)" value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} data-testid="watch-name-input" />
          <Input placeholder="Stichworte: immobilien, treuhand" value={nw.keywords} onChange={(e) => setNw({ ...nw, keywords: e.target.value })} data-testid="watch-keywords-input" />
          <Input placeholder="Kantone: ZH, ZG, SG" value={nw.cantons} onChange={(e) => setNw({ ...nw, cantons: e.target.value })} data-testid="watch-cantons-input" />
          <Input type="number" placeholder="Radius km um Wetzikon" value={nw.radius_km} onChange={(e) => setNw({ ...nw, radius_km: e.target.value })} data-testid="watch-radius-input" />
          <Button onClick={create} disabled={busy === "create"} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="watch-create-btn">
            {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellPlus className="w-4 h-4 mr-1" />} Alarm anlegen
          </Button>
        </div>
        <p className="text-[11px] text-slate-400">Stichworte durchsuchen Firmenname UND Firmenzweck. Radius = Distanz des Firmensitzes zu Wetzikon. Leere Felder = kein Filter.</p>
        {watches.map((w) => (
          <div key={w.id} className="border border-[var(--studio-border)] rounded-lg p-3 space-y-2" data-testid={`watch-row-${w.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex-1 min-w-[220px]">
                <div className="text-sm font-medium flex items-center gap-2">
                  {w.name}
                  <Badge className={`text-[9px] ${w.enabled ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`} data-testid={`watch-status-${w.id}`}>
                    {w.enabled ? "Aktiv — sucht automatisch" : "Inaktiv"}
                  </Badge>
                  <span className="text-[10px] text-slate-400">→ Leads zu {companyName(w.company_id)}</span>
                </div>
                <div className="text-[11px] text-slate-500">
                  {w.keywords?.length ? `Stichworte: ${w.keywords.join(", ")}` : "Alle Branchen"}
                  {w.cantons?.length ? ` · Kantone: ${w.cantons.join(", ")}` : ""}
                  {w.radius_km ? ` · ${w.radius_km} km um Wetzikon` : ""}
                </div>
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => runNow(w.id)} disabled={busy === w.id} data-testid={`watch-run-${w.id}`}>
                {busy === w.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 mr-1" />} Jetzt prüfen (7 Tage)
              </Button>
              <Switch checked={!!w.enabled} onCheckedChange={() => toggle(w)} data-testid={`watch-toggle-${w.id}`} />
              <button onClick={() => del(w.id)} className="text-red-400 hover:text-red-600" data-testid={`watch-delete-${w.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] bg-[var(--studio-bg)] rounded p-2" data-testid={`watch-stats-${w.id}`}>
              <div><p className="text-slate-400">Suchintervall</p><p className="font-medium">stündl. Prüfung, max. 1 Lauf / 20 h</p></div>
              <div><p className="text-slate-400">Letzter Suchlauf</p><p className="font-medium">{w.last_run ? new Date(w.last_run).toLocaleString("de-CH") : "noch nie"}</p></div>
              <div><p className="text-slate-400">Nächster Suchlauf</p><p className="font-medium">{!w.enabled ? "— (inaktiv)" : w.next_run ? `ab ${new Date(w.next_run).toLocaleString("de-CH")}` : "beim nächsten Prüflauf"}</p></div>
              <div><p className="text-slate-400">Erstellte Leads gesamt</p>
                <p className="font-medium">{w.leads_created ?? w.total_created ?? 0}{" "}
                  {(w.leads_created ?? 0) > 0 && <a href="/leads" className="text-[#2563EB] hover:underline">öffnen →</a>}
                </p></div>
            </div>
            {w.last_run && (
              <p className="text-[11px] text-slate-500">
                Letzter Lauf: {w.last_checked ?? 0} Neueintragungen geprüft · <b className="text-emerald-600">{w.last_created ?? 0} neue Leads</b> · {w.last_known ?? 0} bereits bekannt
              </p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function ZefixRadarPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [q, setQ] = useState("");
  const [radius, setRadius] = useState("");
  const [results, setResults] = useState(null);
  const [onlyActive, setOnlyActive] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => { setCompanies(r.data || []); if (r.data?.length) setCompanyId(r.data[0].id); })
      .catch(() => {});
  }, []);

  const search = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 3) { toast.error("Mindestens 3 Zeichen eingeben"); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/zefix/search`, { params: { q: q.trim(), limit: 30, radius_km: radius ? parseInt(radius) : 0 }, withCredentials: true });
      setResults(r.data.results);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Suche fehlgeschlagen");
    } finally { setLoading(false); }
  };

  const shown = (results || []).filter((r) => !onlyActive || r.active);

  return (
    <div className="p-6 space-y-5" data-testid="zefix-radar-page">
      <PageHelp
        title="Firmen-Radar (Handelsregister)"
        text="Durchsuche das Schweizer Handelsregister (Zefix) nach Firmen — z.B. 'Immobilien Zürich' — und übernimm interessante Treffer mit einem Klick als Akquise-Leads inkl. Adresse, UID und Firmenzweck. Perfekt für gezielte Outreach-Kampagnen (LinkedIn, Mailing). Eingehende Leads mit Firmennamen werden automatisch gegen das Handelsregister geprüft."
      />
      <div className="flex flex-wrap items-center gap-3">
        <Radar className="w-6 h-6 text-[#2563EB]" />
        <div>
          <h1 className="text-2xl font-bold">Firmenradar</h1>
          <p className="text-xs text-slate-500">Sucht nach potenziellen Geschäftskunden anhand definierter Regionen, Branchen und Suchkriterien (Quelle: Zefix/Handelsregister).</p>
        </div>
        <div className="flex-1" />
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-56" data-testid="zefix-company-select"><SelectValue placeholder="Leads zuordnen zu…" /></SelectTrigger>
          <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>Leads → {c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="pt-5">
          <form onSubmit={search} className="flex flex-wrap gap-2">
            <Input
              data-testid="zefix-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='z.B. "Immobilien Zürich", "Treuhand Zug", Firmenname…'
              className="flex-1 min-w-[240px]"
            />
            <Input type="number" value={radius} onChange={(e) => setRadius(e.target.value)}
              placeholder="Radius km um Wetzikon" className="w-44" data-testid="zefix-radius-input" />
            <Button type="submit" disabled={loading} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="zefix-search-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
              Suchen
            </Button>
            <Button type="button" variant={onlyActive ? "default" : "outline"} className={onlyActive ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              onClick={() => setOnlyActive((v) => !v)} data-testid="zefix-active-toggle">
              Nur aktive
            </Button>
          </form>
        </CardContent>
      </Card>

      <WatchSection companies={companies} companyId={companyId} />

      {results !== null && (
        <div className="space-y-2" data-testid="zefix-results">
          <p className="text-xs text-slate-500">{shown.length} Treffer{onlyActive ? " (nur aktive)" : ""}{radius ? ` · Radius ${radius} km um Wetzikon` : ""} — Quelle: Zefix / Handelsregister</p>
          {shown.length === 0 && <Card><CardContent className="py-6 text-center text-sm text-slate-400">Keine Treffer.</CardContent></Card>}
          {shown.map((r) => <ResultRow key={r.ehraid} r={r} companyId={companyId} />)}
        </div>
      )}
    </div>
  );
}
